import React, { useEffect, useRef, useState } from "react";
import { Mail, Check, Loader2, X, Play, Pause, Volume2 } from "lucide-react";
import { api, API } from "../../lib/api";

const KEY = "pursue.subscribed";
const SAMPLE_AUDIO_URL = `${API}/static_files/audio/NAVY-TIC-TAC-2004_analyst.mp3`;

export default function SubscribeBanner() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const dismissed = localStorage.getItem(KEY);
    if (!dismissed) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/subscribe", { email: email.trim() }).then((x) => x.data);
      setResult(r);
      localStorage.setItem(KEY, JSON.stringify({ email: r.email, at: Date.now() }));
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Could not subscribe." });
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(KEY, JSON.stringify({ dismissed: true, at: Date.now() }));
    setOpen(false);
    if (audioRef.current) audioRef.current.pause();
  };

  const toggleSample = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  if (!open) return null;
  return (
    <div
      data-testid="subscribe-banner"
      className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[92vw] border border-amber-500/40 bg-zinc-950 shadow-2xl"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-amber-400" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-200">
            Tranche Alerts
          </span>
        </div>
        <button
          data-testid="subscribe-dismiss"
          onClick={dismiss}
          className="text-zinc-500 hover:text-amber-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="p-4">
        {result?.subscribed ? (
          <div className="flex items-start gap-2 text-sm text-zinc-200">
            <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400 mb-1">
                {result.already ? "Already on list" : "Confirmed"}
              </div>
              <div className="text-xs text-zinc-300">
                You'll be emailed when war.gov posts a new declassified UAP tranche.
              </div>
              {result.note && (
                <div className="text-[10px] text-zinc-500 mt-2 font-mono">
                  {result.note}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-200 leading-snug mb-3">
              <span className="text-amber-400 font-semibold">War.gov</span> releases new tranches every few weeks. Get pinged the moment we index them.
            </p>
            <button
              type="button"
              data-testid="subscribe-sample-btn"
              onClick={toggleSample}
              className="flex items-center gap-2 w-full mb-3 px-3 py-2 border border-zinc-800 hover:border-amber-400/60 transition-colors group"
              title="Hear the Onyx analyst voice that narrates declassified briefs"
            >
              <span className="h-7 w-7 rounded-full bg-amber-500 group-hover:bg-amber-400 text-zinc-950 flex items-center justify-center shrink-0">
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-0.5" />}
              </span>
              <span className="flex-1 text-left">
                <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 group-hover:text-amber-400">
                  ◆ Sample · Onyx voice
                </span>
                <span className="block text-xs text-zinc-200">Tic Tac Incident · 1:29</span>
              </span>
              <Volume2 className="h-3.5 w-3.5 text-zinc-500 group-hover:text-amber-400" />
            </button>
            <audio
              ref={audioRef}
              src={SAMPLE_AUDIO_URL}
              preload="none"
              onEnded={() => setPlaying(false)}
              data-testid="subscribe-sample-audio"
            />
            <form onSubmit={submit} className="flex gap-2">
              <input
                data-testid="subscribe-email-input"
                type="email"
                required
                placeholder="analyst@domain.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="subscribe-submit"
                className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest"
              >
                {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Subscribe"}
              </button>
            </form>
            {result?.error && (
              <div className="mt-2 font-mono text-[10px] text-red-400">{result.error}</div>
            )}
            <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-zinc-600">
              No spam · unsubscribe anytime
            </div>
          </>
        )}
      </div>
    </div>
  );
}
